// Listens for `beforeinstallprompt` and renders a small "Install" chip
// in the AppShell footer. Hidden after install or after the user
// dismisses it (7-day cookie so we don't pester).
//
// On Chrome / Edge the event fires when the SW + manifest pass the
// installability heuristic. On iOS Safari there's no event — the user
// must use Share → Add to Home Screen — so we don't render the chip
// there. Standalone-mode detection short-circuits in either case.

import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const DISMISS_KEY = 'lc-install-dismissed';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function recentlyDismissed(): boolean {
  try {
    const raw = window.localStorage?.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch { return false; }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari exposes the standalone flag on navigator
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window.navigator as any).standalone === true;
}

export function InstallHint() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState(() => isStandalone() || recentlyDismissed());

  useEffect(() => {
    if (hidden) return;
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setEvt(null);
      setHidden(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [hidden]);

  if (hidden || !evt) return null;

  const dismiss = () => {
    try { window.localStorage?.setItem(DISMISS_KEY, String(Date.now())); } catch { /* */ }
    setHidden(true);
  };

  const install = async () => {
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      if (choice.outcome === 'accepted') {
        setEvt(null);
        setHidden(true);
      } else {
        dismiss();
      }
    } catch {
      dismiss();
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-surface)] text-xs">
      <button
        onClick={install}
        className="inline-flex items-center gap-1.5 text-[var(--accent-brand)] font-display font-medium"
      >
        <Download className="w-3 h-3" />
        Install Phos/cloud
      </button>
      <button
        onClick={dismiss}
        aria-label="dismiss"
        className="text-[var(--fg-faint)] hover:text-[var(--fg-primary)]"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
